import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useSearchParams, useNavigate } from "react-router-dom";

import {
  Button,
  Drawer,
  DripLockedModal,
  Loader,
  SearchBar,
} from "@/components";
import { useResponsiveness, useTranslate } from "@/providers/context";
import { DRAWER_SEARCH } from "@/const.ts";
import { Slider } from "@/icons";
import { Header } from "@/app/modules/Navigation";

import { DurationSlider } from "./components/DurationSlider.tsx";
import { EmptyResults } from "./components/EmptyResults";
import { FilterSection } from "./components/FilterSection.tsx";
import { SearchResults } from "./components/SearchResults";
import { LoadingFilterSection } from "./components/LoadingFilterSection.tsx";
import { DURATION_PARAM, FILTER_PARAM, SEARCH_PARAM } from "./const.ts";
import { useFilters } from "./hooks/useFilters.tsx";
import { useSearch } from "./hooks/useSearch.tsx";
import { XIcon } from "lucide-react";

export const SearchScreen = () => {
  const { data: filters } = useFilters();
  const [searchParams, setSearchParams] = useSearchParams();
  const { openDrawer, setOpenDrawer, drawerMargin, transitionStyle } =
    useResponsiveness();
  const { translate } = useTranslate();

  const [dripLockedModalOpen, setDripLockedModalOpen] = useState(false);
  const { state, search } = useLocation();
  const navigate = useNavigate();
  const isDripLocked = state?.isDripLocked;

  // If a drip locked collection is navigated to, it will navigate back to search with isDripLocked in location state
  // Use this to open the drip locked modal
  useEffect(() => {
    if (isDripLocked) {
      setDripLockedModalOpen(true);
      // Clear isDripLocked from location state
      navigate(`/search${search}`, {
        replace: true,
        state: { ...state, isDripLocked: undefined },
      });
    }
  }, [isDripLocked, search, setDripLockedModalOpen, navigate, state]);

  const queryParameters = useMemo(
    () => new URLSearchParams(searchParams),
    [searchParams],
  );

  const handleSearch = useCallback(
    (value: string) => {
      if (value !== "") {
        queryParameters.set(SEARCH_PARAM, value);
      } else {
        queryParameters.delete(SEARCH_PARAM);
      }
      setSearchParams(queryParameters);
    },
    [queryParameters, setSearchParams],
  );

  const filtersByCategory = useMemo(() => {
    const response: string[][] = [];
    for (const [name, value] of Array.from(searchParams)) {
      if (name.startsWith(FILTER_PARAM)) {
        response.push(value.split("|").filter(Boolean));
      }
    }
    return response;
  }, [searchParams]);
  const filterArrayLength = Object.values(filtersByCategory).reduce(
    (acc, curr) => acc + curr.length,
    0,
  );
  const selectedFiltersLabel =
    filterArrayLength > 0
      ? `(${filterArrayLength} ${translate("selected")})`
      : "";

  const query = searchParams.get(SEARCH_PARAM) ?? "";
  const [min_duration, max_duration] = searchParams
    .get(DURATION_PARAM)
    ?.split(",") || [undefined, undefined];

  const { data, isLoading } = useSearch({
    query,
    tags_by_category: filtersByCategory,
    min_duration: min_duration ? parseInt(min_duration) * 60 : undefined,
    max_duration: max_duration ? parseInt(max_duration) * 60 : undefined,
  });

  const { videos = [], collections = [] } = data || {};

  const total = useMemo(
    () => videos.length + collections.length,
    [videos, collections],
  );

  const clearAllFilters = useCallback(() => {
    const keysArray = Array.from(queryParameters.keys());
    for (const key of keysArray) {
      if (key.startsWith(FILTER_PARAM) || key === DURATION_PARAM) {
        queryParameters.delete(key);
      }
    }
    setSearchParams(queryParameters);
  }, [queryParameters, setSearchParams]);

  return (
    <div className="px-[28px]">
      <Header
        content={
          <div className="flex p-[1px]">
            <SearchBar value={query} onSearch={handleSearch} />
            {
              <Button
                variant="tertiary"
                size="m"
                className="p2-semibold"
                icon={<Slider width={20} height={20} />}
                iconPosition="left"
                onClick={() => setOpenDrawer(DRAWER_SEARCH)}
              >
                {`${translate("Filter")} ${selectedFiltersLabel}`}
              </Button>
            }
          </div>
        }
      />
      <div style={{ marginRight: drawerMargin, transition: transitionStyle }}>
        {isLoading ? (
          <div className="flex flex-col items-center pt-[335px]">
            <Loader className="h-12 w-12" />
          </div>
        ) : total === 0 ? (
          <EmptyResults query={query} filtersSelected={filterArrayLength > 0} />
        ) : (
          <div>
            <SearchResults
              videos={videos}
              collections={collections}
              total={total}
            />
          </div>
        )}
        <Drawer
          title={translate("Filter")}
          open={openDrawer === DRAWER_SEARCH}
          onOpenChange={(open) => setOpenDrawer(open ? DRAWER_SEARCH : "")}
        >
          <div className="flex h-full flex-col justify-between pb-5">
            <div>
              <DurationSlider />
              {filters ? (
                filters?.map((f) => <FilterSection key={f.Id} filter={f} />)
              ) : (
                <>
                  <LoadingFilterSection />
                  <LoadingFilterSection />
                  <LoadingFilterSection />
                </>
              )}
            </div>
            <div>
              <Button
                icon={<XIcon />}
                iconPosition="left"
                variant="tertiary"
                className="capitalize"
                onClick={clearAllFilters}
              >
                {translate("Clear filters")}
              </Button>
            </div>
          </div>
        </Drawer>
      </div>
      <DripLockedModal
        open={dripLockedModalOpen}
        setOpen={setDripLockedModalOpen}
      />
    </div>
  );
};
